/**
 * Battery utility functions
 * Uses the Battery Status API when available, falls back to default values
 */

/**
 * Get battery status with health-based color coding
 * @returns {Promise<{level: number, charging: boolean, tone: string, value: string}>}
 */
export const getBatteryStatus = async () => {
  try {
    // Check if Battery API is available
    if ('getBattery' in navigator) {
      const battery = await navigator.getBattery();
      
      const level = Math.round(battery.level * 100);
      const charging = battery.charging;
      
      // Determine tone based on battery level
      let tone = 'ok';
      if (level <= 20) {
        tone = 'error';
      } else if (level <= 40) {
        tone ='warning';
      }
      
      // Format value string
      let value = `${level}%`;
      if (charging) {
        value += ' (Charging)';
      }
      
      return {
        level,
        charging,
        tone,
        value,
      };
    }
  } catch (error) {
    console.warn('Battery API not available:', error);
  }
  
  // Fallback: return default values
  return {
    level: 100,
    charging: false,
    tone: 'ok',
    value: 'N/A',
  };
};

/**
 * Hook-like function to monitor battery status with callbacks
 * @param {Function} callback - Function to call when battery status changes
 * @returns {Function} Cleanup function to stop monitoring
 */
export const monitorBatteryStatus = (callback) => {
  let battery = null;
  let listeners = [];
  
  const updateBatteryStatus = async () => {
    try {
      if ('getBattery' in navigator) {
        battery = await navigator.getBattery();
        
        const updateStatus = async () => {
          const status = await getBatteryStatus();
          callback(status);
        };
        
        // Set up event listeners
        const chargingChangeListener = () => updateStatus();
        const levelChangeListener = () => updateStatus();
        
        battery.addEventListener('chargingchange', chargingChangeListener);
        battery.addEventListener('levelchange', levelChangeListener);
        
        listeners = [
          { event: 'chargingchange', handler: chargingChangeListener },
          { event: 'levelchange', handler: levelChangeListener },
        ];
        
        // Initial update
        await updateStatus();
      } else {
        // Fallback: call once with default
        callback(await getBatteryStatus());
      }
    } catch (error) {
      console.warn('Battery monitoring not available:', error);
      callback(await getBatteryStatus());
    }
  };
  
  updateBatteryStatus();
  
  // Return cleanup function
  return () => {
    if (battery && listeners.length > 0) {
      listeners.forEach(({ event, handler }) => {
        battery.removeEventListener(event, handler);
      });
    }
  };
};



 
